"use client";

import useFilterStore from "@/stores/order-filter.store";
import { useSearchParams } from "next/navigation";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { useCallback, useEffect, useMemo } from "react";

interface PaginationProps {
  totalItems: number;
  itemsPerPage: number;
}

const Pagination = ({ totalItems, itemsPerPage }: PaginationProps) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const filter = useFilterStore((s) => s.filter);
  const updateFilter = useFilterStore((s) => s.updateFilter);
  const searchParams = useSearchParams();

  const currentPage = filter.page || 1;
  const initialPage = useMemo(() => {
    const pageParam = searchParams.get("page");
    return pageParam ? parseInt(pageParam, 10) : 1;
  }, [searchParams]);
  useEffect(() => {
    updateFilter({ page: initialPage });
  }, [initialPage, updateFilter]);

  const onPageChange = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages) return;
      const newFilter = { page: page };
      updateFilter(newFilter);
    },
    [totalPages, updateFilter],
  );

  if (totalPages <= 1 || totalItems === 0) return null;

  const pageNumbers: number[] = [];
  const maxPagesToShow = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
  const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

  if (endPage - startPage < maxPagesToShow - 1) {
    startPage = Math.max(1, endPage - maxPagesToShow + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="mt-4 flex flex-col items-center justify-between gap-2 sm:flex-row">
      <div className="w-full sm:w-auto">
        <Message
          severity="contrast"
          text={`Showing ${(currentPage - 1) * itemsPerPage + 1} - ${Math.min(
            currentPage * itemsPerPage,
            totalItems,
          )} of ${totalItems} items`}
          className="w-full text-sm sm:w-auto"
        />
      </div>
      <div className="flex w-full items-center justify-center gap-2 space-x-2 sm:w-auto">
        <Button
          size="small"
          icon="pi pi-chevron-left"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="min-w-[2rem]"
        />
        {startPage > 1 && (
          <>
            <Button
              size="small"
              label="1"
              onClick={() => onPageChange(1)}
              outlined={currentPage !== 1}
              className="min-w-[2rem]"
            />
            {startPage > 2 && (
              <span className="text-sm text-gray-500">...</span>
            )}
          </>
        )}
        {pageNumbers.map((page) => (
          <Button
            size="small"
            outlined={currentPage !== page}
            key={page}
            label={page.toString()}
            onClick={() => onPageChange(page)}
            className="min-w-[2rem]"
          />
        ))}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && (
              <span className="text-sm text-gray-500">...</span>
            )}
            <Button
              size="small"
              label={totalPages.toString()}
              onClick={() => onPageChange(totalPages)}
              outlined={currentPage !== totalPages}
              className="min-w-[2rem]"
            />
          </>
        )}
        <Button
          size="small"
          icon="pi pi-chevron-right"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="min-w-[2rem]"
        />
      </div>
    </div>
  );
};

export default Pagination;
